import { ChevronDown, Filter, Search, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useShell } from '../../shell/slots.tsx';

export interface Kpi {
  label: string;
  value: ReactNode;
  /** Variação ("+8%") e se ela é boa ou ruim. */
  delta?: { text: string; good: boolean };
  note?: string;
  icon?: LucideIcon;
}

/**
 * Os números do topo de uma tela (ADR 0133), no jeito do layout (`kpi`): cartões separados, uma
 * faixa única dividida, ou nada. Cada número tem o rótulo, a variação e uma nota de apoio.
 */
export function KpiRow({
  items,
  mode,
}: {
  items: readonly Kpi[];
  mode?: 'card' | 'strip' | 'none';
}) {
  const shell = useShell();
  const kind = mode ?? shell?.options.kpi ?? 'card';
  if (kind === 'none' || items.length === 0) return null;
  const one = (k: Kpi, i: number, strip: boolean) => (
    <div
      key={i}
      className={`flex min-w-0 flex-col gap-1 p-4 ${strip ? (i ? 'border-l border-rule' : '') : 'ui-panel rounded-lg bg-sheet'}`}
    >
      <span className="flex items-center justify-between gap-2 text-xs text-muted">
        {k.label}
        {k.icon && <k.icon size={15} aria-hidden className="ui-icon-support" />}
      </span>
      <span className="truncate text-xl font-semibold tabular-nums">{k.value}</span>
      {(k.delta || k.note) && (
        <span className="text-xs text-muted">
          {k.delta && (
            <em className={`font-medium not-italic ${k.delta.good ? 'text-ok' : 'text-danger'}`}>
              {k.delta.text}
            </em>
          )}
          {k.delta && k.note && ' · '}
          {k.note}
        </span>
      )}
    </div>
  );
  if (kind === 'strip') {
    return (
      <div className="ui-panel grid grid-cols-2 rounded-lg bg-sheet lg:grid-cols-4">
        {items.map((k, i) => one(k, i, true))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {items.map((k, i) => one(k, i, false))}
    </div>
  );
}

export interface FilterChip {
  label: string;
  value: string;
}

/**
 * A busca e os filtros de uma lista (ADR 0133), no jeito do layout (`filters`): uma barra de
 * campos, etiquetas que se ligam e desligam, ou uma coluna à esquerda (facetas). O conteúdo da
 * lista vai em `children`, para a coluna de facetas ficar ao lado dele.
 */
export function FilterBar({
  search,
  filters,
  mode,
  children,
}: {
  search: string;
  filters: readonly FilterChip[];
  mode?: 'bar' | 'chips' | 'facets';
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const shell = useShell();
  const kind = mode ?? shell?.options.filters ?? 'bar';
  const searchBox = (
    <span className="ui-control ui-input flex min-w-48 flex-1 items-center gap-2 border px-3 text-sm text-muted">
      <Search size={15} aria-hidden />
      {search}
    </span>
  );
  if (kind === 'facets') {
    return (
      <div className="grid gap-4 lg:grid-cols-[13rem_minmax(0,1fr)]">
        <aside className="flex flex-col gap-4 text-sm">
          {searchBox}
          {filters.map((f) => (
            <div key={f.label} className="flex flex-col gap-1.5">
              <h4 className="ui-cap text-xs font-medium text-muted">{f.label}</h4>
              <span className="flex items-center gap-2">
                <span className="size-3.5 rounded border border-accent bg-accent" aria-hidden />
                {f.value}
              </span>
            </div>
          ))}
        </aside>
        <div className="flex min-w-0 flex-col gap-3">{children}</div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {searchBox}
        {kind === 'chips'
          ? filters.map((f, i) => (
              <span
                key={f.label}
                className={`rounded-full border px-3 py-1 text-xs ${i === 0 ? 'border-accent bg-brand-soft text-brand-text' : 'border-rule'}`}
              >
                {f.label}: <b className="font-medium">{f.value}</b>
              </span>
            ))
          : filters.map((f) => (
              <span
                key={f.label}
                className="ui-control ui-input flex items-center gap-2 border px-3 text-sm"
              >
                <span className="text-muted">{f.label}:</span> {f.value}
                <ChevronDown size={14} aria-hidden className="text-muted" />
              </span>
            ))}
        <span className="flex items-center gap-1.5 px-2 text-sm text-muted">
          <Filter size={14} aria-hidden />
          {t('patterns.filters.more')}
        </span>
      </div>
      {children}
    </div>
  );
}
